import React, { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import generator from 'generate-password';

// Hooks
import useCollaborationSettingsLink from '@/hooks/swr/useCollaborationSettingsLink';
import useTranslation from '@/utils/hooks/translation/useTranslation';

//lib
import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useToggle from '@/hooks/utils/useToggle';

import { PENDO_CLASS_SCHEDULER_COPY_SHARE_LINK } from '@/lib/tools/pendo/class';

// Components
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
import Loader from '@/components/base/Loader';
import Switch from '@/components/base/Switch';
import Tooltip from '@/components/base/Tooltip';
import RefreshConfirmationModal from '@/components/pages/scheduler/collaboration/settings/modals/RefreshConfirmationModal';

import {
    COLLABORATION_SECURE_LINK,
    COLLABORATION_SECURE_LINK_GROUP,
    COLLABORATION_SHARING_LINK,
    COLLABORATION_SHARING_LINK_GROUP
} from '@/routes/apiEndpoints';

const CollaborationLinkSettings = ({ selectedProfile }) => {
    const linkRef = useRef(null);
    const passwordRef = useRef(null);
    const { t } = useTranslation('scheduler');
    const { isOpen: isOpenConfirmModal, toggle: toggleConfirmModal } = useToggle({
        initial: false
    });
    const isGroup = selectedProfile?.group;
    const { link, mutateLink, linkError, isLoading } = useCollaborationSettingsLink({
        [isGroup ? 'activeGroupId' : 'SPID']: selectedProfile?.id,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false }
    });
    const [password, setPassword] = useState({
        isSecured: false,
        isLoading: false,
        error: null,
        value: '•••••••••••••••'
    });
    const [copiedToClipboard, setCopiedToClipboard] = useState(false);

    const onRefreshLink = async () => {
        const pwd = onGeneratePassword();
        setPassword((prevState) => ({
            ...prevState,
            isLoading: true,
            error: null
        }));
        try {
            await post(
                injectEndpointUrlParams(
                    isGroup ? COLLABORATION_SHARING_LINK_GROUP : COLLABORATION_SHARING_LINK,
                    {
                        SPID: selectedProfile?.id,
                        GROUP_ID: selectedProfile?.id
                    }
                ),
                { ...(password.isSecured && { password: pwd }) }
            );
            await mutateLink();
            toggleConfirmModal();
            setPassword((prevState) => ({
                ...prevState,
                isLoading: false,
                value: pwd
            }));
        } catch (e) {
            setPassword((prevState) => ({
                ...prevState,
                isLoading: false,
                error: true
            }));
        }
    };

    const onRefreshSecurity = async (pwd = '') => {
        setPassword((prevState) => ({
            ...prevState,
            error: null
        }));
        try {
            await post(
                injectEndpointUrlParams(
                    isGroup ? COLLABORATION_SECURE_LINK_GROUP : COLLABORATION_SECURE_LINK,
                    {
                        SPID: selectedProfile?.id,
                        GROUP_ID: selectedProfile?.id,
                        LINK_ID: link?.id
                    }
                ),
                { ...(pwd && { password: pwd }) }
            );
            if (pwd) {
                setPassword((prevState) => ({
                    ...prevState,
                    isLoading: false,
                    value: pwd
                }));
            }
        } catch (e) {
            setPassword((prevState) => ({
                ...prevState,
                isLoading: false,
                error: true
            }));
        }
    };

    const onCopy = (ref) => {
        if (ref.current) {
            ref.current.select();
            navigator.clipboard.writeText(ref.current.value);
            setCopiedToClipboard(true);

            setTimeout(() => {
                setCopiedToClipboard(false);
            }, 1000);
        }
    };

    const onGeneratePassword = () => {
        let pwd = '';
        let pattern = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[-+_!@#$%^&*.,?]).+$');
        while (!pattern.test(pwd)) {
            pwd = generator.generate({
                length: 15,
                lowercase: true,
                uppercase: true,
                numbers: true,
                symbols: true
            });
        }

        return pwd;
    };

    const onSwitchPassword = () => {
        setPassword((prevState) => ({
            ...prevState,
            isSecured: !prevState.isSecured
        }));

        if (!password.isSecured) {
            const pwd = onGeneratePassword();
            setPassword((prevState) => ({
                ...prevState,
                value: pwd
            }));
            onRefreshSecurity(pwd);
        } else {
            onRefreshSecurity();
        }
    };

    useEffect(() => {
        setPassword((prevState) => ({
            ...prevState,
            isSecured: link?.is_secured
        }));
    }, [link]);

    return (
        <>
            {linkError || password.error ? (
                <InfoBanner
                    className="mb-4"
                    title={t('errors:error_occured')}
                    message={t('errors:error_occured_fetch_data')}
                    onClose={() => {
                        setPassword((prevState) => ({
                            ...prevState,
                            error: null
                        }));
                    }}
                />
            ) : null}
            <Label className="mb-2.5" htmlFor="sharing-link">
                <span className="font-GBold text-base text-icoBlack dark:text-white">
                    {t('collaboration.settings.sharing_link_label')}
                </span>
            </Label>
            <Input
                readOnly
                ref={linkRef}
                disabled={isLoading}
                // add ternary to prevent warning about uncontrolled input changing to controlled
                value={
                    link?.hash
                        ? `${process.env.NEXT_PUBLIC_BASE_URL}collaborate-area/${link?.hash}`
                        : ''
                }
                id="sharing-link"
                data-testid="sharing-link"
                afterInput={
                    <div className="flex items-center space-x-4">
                        {isLoading ? <Loader className="mr-1 h-3 w-3" /> : null}
                        <span>
                            <Tooltip
                                content={
                                    copiedToClipboard
                                        ? t('collaboration.settings.copied_to_clipboard')
                                        : t('collaboration.settings.copy_sharing_link')
                                }
                                trigger="mouseenter"
                                placement="top"
                                interactive={true}
                                hideOnClick={false}
                                popperOptions={{
                                    // prevent tooltip to pop in the wrong place
                                    strategy: 'fixed'
                                }}>
                                <button
                                    data-testid="copy-sharing-link"
                                    className={clsx(
                                        'text-sm text-icoGray-500',
                                        PENDO_CLASS_SCHEDULER_COPY_SHARE_LINK
                                    )}
                                    onClick={() => {
                                        if (link?.hash) {
                                            onCopy(linkRef);
                                        }
                                    }}>
                                    <Icon icon="duplicate" />
                                </button>
                            </Tooltip>
                        </span>
                        <button
                            data-testid="refresh-sharing-link"
                            className="text-sm text-icoGray-500"
                            id="refresh-sharing-link"
                            onClick={toggleConfirmModal}>
                            <Icon icon="refresh" />
                        </button>
                    </div>
                }
            />
            {/* Password */}
            <div
                className={clsx(
                    'inset-0 mt-4 flex h-12 w-full items-center justify-between border border-icoGray-300 px-4 dark:border-icoDarkMode-beton',
                    {
                        'rounded-tl-md rounded-tr-md': password.isSecured,
                        'rounded-md': !password.isSecured,
                        'cursor-not-allowed bg-icoGray-300 text-icoGray-400 dark:bg-icoDarkMode-beton':
                            isLoading
                    }
                )}>
                <div className="flex items-center space-x-3">
                    <Icon
                        icon="locked-on-outline"
                        className={clsx({
                            'text-icoBlue': password.isSecured,
                            'text-icoGray-500': !password.isSecured
                        })}
                    />
                    <p>{t('collaboration.settings.secure_link_with_password')}</p>
                    <span>
                        <Tooltip
                            content={t('collaboration.settings.password_tooltip')}
                            placement="top"
                            interactive={true}
                            popperOptions={{
                                // prevent tooltip to pop in the wrong place
                                strategy: 'fixed'
                            }}>
                            <Icon
                                aria-label={t('collaboration.settings.password_tooltip')}
                                icon="help"
                                className="text-xs text-icoGray-400 hover:text-icoGray-500 dark:text-icoDarkMode-wolf"
                            />
                        </Tooltip>
                    </span>
                </div>
                <div className="flex items-center space-x-3">
                    <Switch.Wrapper
                        checked={password.isSecured}
                        disabled={isLoading}
                        data-testid="switch-password">
                        <Switch.Label>
                            {password.isSecured
                                ? t('collaboration.settings.password_activated')
                                : t('collaboration.settings.password_deactivated')}
                        </Switch.Label>
                        <Switch
                            onChange={onSwitchPassword}
                            ariaLabel={
                                t('collaboration.settings.secure_link_with_password') +
                                ' ' +
                                selectedProfile.id
                            }
                        />
                    </Switch.Wrapper>
                </div>
            </div>
            {password.isSecured ? (
                <Input
                    ref={passwordRef}
                    disabled={password.isLoading}
                    readOnly
                    inputStyle="dropdown"
                    value={password.value}
                    id="sharing-link-password"
                    data-testid="sharing-link-password"
                    className="rounded-bl-md rounded-br-md border"
                    afterInput={
                        <div className="flex items-center space-x-4">
                            {password.isLoading ? <Loader className="mr-1 h-3 w-3" /> : null}
                            <span>
                                <Tooltip
                                    content={
                                        copiedToClipboard
                                            ? t('collaboration.settings.copied_to_clipboard')
                                            : t('collaboration.settings.copy_password')
                                    }
                                    trigger="mouseenter"
                                    placement="top"
                                    interactive={true}
                                    hideOnClick={false}
                                    popperOptions={{
                                        // prevent tooltip to pop in the wrong place
                                        strategy: 'fixed'
                                    }}>
                                    <button
                                        data-testid="copy-password"
                                        className="text-sm text-icoGray-500"
                                        onClick={() => onCopy(passwordRef)}>
                                        <Icon icon="duplicate" />
                                    </button>
                                </Tooltip>
                            </span>
                            <button
                                data-testid="refresh-password"
                                disabled={password.isLoading}
                                className="text-sm text-icoGray-500"
                                id="refresh-password"
                                onClick={() => {
                                    const pwd = onGeneratePassword();
                                    setPassword((prevState) => ({
                                        ...prevState,
                                        isLoading: true
                                    }));
                                    onRefreshSecurity(pwd);
                                }}>
                                <Icon icon="refresh" />
                            </button>
                        </div>
                    }
                />
            ) : null}
            <RefreshConfirmationModal
                isLoading={password.isLoading}
                toggle={toggleConfirmModal}
                isOpen={isOpenConfirmModal}
                onConfirm={onRefreshLink}
            />
        </>
    );
};

export default CollaborationLinkSettings;
